import { Observable, of, merge } from "rxjs"
import { switchMap, catchError, filter, delayWhen } from "rxjs/operators"
import { ofType, StateObservable } from "redux-observable"
import type { EpicDependencies } from "../../../../rxjs/configureEpics"
import {
    FETCH_CUSTOMERS_FEEDBACK_REPORT_REQUESTED,
    fetchCustomersFeedbackReportFailure,
    fetchCustomersFeedbackReportRequested,
    fetchCustomersFeedbackReportSuccess,
} from "../../../../../api/actions"
import { getHttpHeaders, getSelectedAccountId } from "../../../../auth/selectors"
import { ACCOUNT_SELECTED, REFETCH_DATA } from "../../../../auth/actions"
import { clearCustomersFeedbackData, CUSTOMER_FEEDBACK_AGENDA_MOUNTED, customerFeedbackAgendaMounted } from "../actions"
import {
    createLoadingEnded,
    createLoadingStarted,
    SET_CUSTOMERS_FEEDBACK_REPORT_DEFAULT_FILTER,
    SET_FILTER_VALUE,
    SET_FILTERS,
    SET_PAGINATION,
    setCustomersFeedbackReportDefaultFilter,
    setPagination,
    setTotalCount,
} from "../../../../filters/actions"
import { isCustomerReportAgendaOpened } from "../customersTable/selectors"
import {
    getCustomersFeedbackByPageNumber,
    getCustomersFeedbackCurrentPage,
    getCustomersFeedbackQueryApiParam,
} from "./selectors"
import { transformCustomersFeedbackReportServerData } from "./utils"
import { waitForAccountSelected, waitForGlobalFilterSet } from "../customersTable/epics"
import { Action, StateObject } from "../../../../redux/types"

export const customersFeedbackAgendaMountedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(CUSTOMER_FEEDBACK_AGENDA_MOUNTED),
        delayWhen(action => waitForGlobalFilterSet(actions$, deps.getState(), action)),
        switchMap(() => {
            return getSelectedAccountId(deps.getState()) ? [setCustomersFeedbackReportDefaultFilter()] : []
        })
    )

export const fetchCustomersReportOnAccountSwitch = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(ACCOUNT_SELECTED),
        filter(() => {
            return isCustomerReportAgendaOpened(deps.getState())
        }),
        switchMap(() => [customerFeedbackAgendaMounted()])
    )

export const nextCustomerPageRequestedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(SET_PAGINATION),
        filter((action: Action) => action.payload.identifier === "customersFeedback"),
        switchMap(action => {
            const pageToGo = action.payload.paginationValue.currentPage

            if (!pageToGo) {
                return []
            }

            const pageData = getCustomersFeedbackByPageNumber(deps.getState(), pageToGo)
            return !pageData || pageData.length === 0 ? [fetchCustomersFeedbackReportRequested()] : []
        })
    )

export const customersFeedbackFilterChangedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    _deps: EpicDependencies
) =>
    merge(
        actions$.pipe(ofType(SET_CUSTOMERS_FEEDBACK_REPORT_DEFAULT_FILTER)),
        actions$.pipe(
            ofType(SET_FILTERS, SET_FILTER_VALUE),
            filter(
                (action: Action) =>
                    action.payload.identifier === "customersFeedback" &&
                    !(action.payload.filters && action.payload.filters.feedbackType !== undefined)
            )
        )
    ).pipe(
        switchMap(() => {
            return [
                clearCustomersFeedbackData(),
                setPagination("customersFeedback", {
                    currentPage: 1,
                }),
            ]
        })
    )

export const customersFeedbackReportLoadingStartedEpics = (
    actions$: Observable<Action>,
    state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(FETCH_CUSTOMERS_FEEDBACK_REPORT_REQUESTED, REFETCH_DATA),
        filter(() => isCustomerReportAgendaOpened(deps.getState())),
        switchMap(() => [createLoadingStarted("customersFeedback")])
    )

export const fetchCustomersFeedbackReportRequestedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(FETCH_CUSTOMERS_FEEDBACK_REPORT_REQUESTED, REFETCH_DATA),
        filter(() => isCustomerReportAgendaOpened(deps.getState())),
        delayWhen((action: Action) => waitForAccountSelected(actions$, deps.getState(), action)),
        switchMap(() =>
            deps.api
                .getCustomersFeedbackReportApi(
                    getHttpHeaders(deps.getState()),
                    getCustomersFeedbackQueryApiParam(deps.getState())
                )
                .pipe(
                    switchMap(response => [
                        createLoadingEnded("customersFeedback"),
                        setTotalCount("customersFeedback", response.total_count),
                        fetchCustomersFeedbackReportSuccess(
                            transformCustomersFeedbackReportServerData(response),
                            getCustomersFeedbackCurrentPage(deps.getState())
                        ),
                    ]),
                    catchError(error => of(fetchCustomersFeedbackReportFailure(error)))
                )
        )
    )

export default [
    customersFeedbackAgendaMountedEpics,
    fetchCustomersReportOnAccountSwitch,
    nextCustomerPageRequestedEpics,
    customersFeedbackFilterChangedEpics,
    customersFeedbackReportLoadingStartedEpics,
    fetchCustomersFeedbackReportRequestedEpics,
]
